import { useEffect, useRef, useState } from 'react'
import { canPlayUnmutedMedia } from '../../autoplay'
import { isDebugEnabled } from '../../App'
import { MediaContent } from 'common/types'
import { LayoutStyleObjectFit } from '../../../../src/types/layout.types'
import { ShakaErrorCode, VideoPlayerManager, VideoPlayerError } from '@novasign/app-video-player'
import type { VideoPlayer as AppVideoPlayer } from '@novasign/app-video-player'
import { getLogger } from '../../logger'
import { useScreen } from './ScreenContainer'
import { updateCacheableVideoUrls, getNativeMediaUrl } from '../../native-bridge'

const log = getLogger('VideoPlayer')

interface VideoPlayerProps {
  content: MediaContent
  preload: boolean
  startTime?: number // in seconds
  objectFit?: LayoutStyleObjectFit
}

export const getShakaErrorCode = (value: any): number | undefined => {
  if (!value || typeof value !== 'object') return undefined
  return value.code ?? value.detail?.code ?? value.error?.code
}

function getUserFacingVideoError(rawError: VideoPlayerError): string {
  const code = getShakaErrorCode(rawError)
  if (code === ShakaErrorCode.HTTP_ERROR && navigator?.onLine === false) {
    return 'No internet connection. Please check your network.'
  }

  return rawError.message || 'Video cannot be played!'
}

export const StreamingVideoPlayer = ({
  content,
  preload,
  startTime,
  objectFit = LayoutStyleObjectFit.FILL,
}: VideoPlayerProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<AppVideoPlayer | null>(null)
  const [displayError, setDisplayError] = useState<string | null>(null)
  const [playerReady, setPlayerReady] = useState(false)

  const screen = useScreen()

  useEffect(() => {
    // Clear previous error so the container div stays in the DOM and
    // containerRef.current is available for the new player instance.
    setDisplayError(null)
    setPlayerReady(false)

    if (!containerRef.current || !content.url) return

    // Rewrite CDN URL through the local proxy before any transformation so
    // that both the manifest and its relative segment URLs are cached.
    let url = getNativeMediaUrl(content.url)
    // TODO: backend shall return direct manifest URL after some weeks
    url = url.replace('/iframe', '/manifest/video.mpd')

    const manager = VideoPlayerManager.getInstance()
    let mounted = true

    const initPlayer = async () => {
      const muted = preload ? true : !canPlayUnmutedMedia()

      // disable ABR iff we have caching mechanism for videos (only Android native for now)
      const disableAbr = (screen?.localPlayback && screen.platform !== 'web') ?? false
      log('Disable ABR:', disableAbr)

      try {
        // Wait until the previous player on the same video element is destroyed if there was any.
        const playerInstance = await manager.createPlayerSafe(
          containerRef.current!,
          {
            url,
            autoplay: true,
            loop: true,
            muted,
            controls: isDebugEnabled(),
            // Disable ABR and lock to fixed quality for consistent caching
            disableAbr,
          },
          {
            onError: (err: VideoPlayerError) => {
              log('Video player error:', err)
              setDisplayError(getUserFacingVideoError(err))
            },
            onReady: () => {
              log('Video player ready')
            },
            onCacheableUrls: (urls: string[]) => {
              log('Received %d segment URLs for content %s', urls.length, content.id)
              updateCacheableVideoUrls(content.id, urls)
            },
          }
        )

        // Only store player if component is still mounted
        if (mounted) {
          playerRef.current = playerInstance
          setPlayerReady(true)
        } else {
          // Component unmounted while we were creating player, clean it up
          await playerInstance.destroy()
        }
      } catch (err: any) {
        log('Failed to create video player:', err)
        if (mounted) {
          const rawMessage = err?.message || 'Failed to initialize video player'
          setDisplayError(getUserFacingVideoError(err ?? rawMessage))
        }
      }
    }

    initPlayer()

    return () => {
      mounted = false
      if (playerRef.current) {
        log('Cleaning up player on effect cleanup', playerRef.current.id)
        playerRef.current.destroy().catch((err: unknown) => {
          log('Error destroying video player on cleanup:', err)
        })
        playerRef.current = null
      }
    }
  }, [content.url, preload])

  useEffect(() => {
    if (playerReady && playerRef.current && startTime) {
      playerRef.current.seek(startTime)
    }
  }, [playerReady, startTime])

  return (
    <div ref={containerRef} className="w-full h-full relative">
      <video
        className="w-full h-full"
        style={{ objectFit }}
        playsInline
        poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7" // a transparent 1x1 pixel data
      />
      {displayError && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
          <div className="text-center text-white p-4">
            <div className="text-4xl mb-2">⚠️</div>
            <div className="text-sm opacity-80">Video Playback Error</div>
            <div className="text-xs opacity-60 mt-1">{displayError}</div>
          </div>
        </div>
      )}
    </div>
  )
}
